import { Box, SvgIcon, Typography, useMediaQuery } from '@mui/material'
import { useEffect, useState, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import AngleLeftIcon from '@hapana/shared/web/styles/assets/svgs/angle-left.svg'
import AngleRightIcon from '@hapana/shared/web/styles/assets/svgs/angle-right.svg'

import { handleStylesWithProps, styles } from './../table.styles'
import TablePaginationSelect from './table-pagination-select.component'

const PAGE_SIZE_FIELD_NAME = 'rowsPerPage'

const TablePagination = ({
  onPageSizeChange,
  canGoBack,
  canGoForward,
  onPreviousPage,
  onNextPage,
  pageSizeOptions,
  totalResultsQuantity,
  page,
  currentPageRecords,
  isLoading,
  hidePaginationUI,
  hideSelectPage,
}) => {
  const { t } = useTranslation('common')
  const formMethods = useForm({
    defaultValues: {
      [PAGE_SIZE_FIELD_NAME]: pageSizeOptions?.[0]?.value || 10,
    },
  })
  const isTablet = useMediaQuery(
    (theme) => theme.breakpoints.up('sm') && theme.breakpoints.down('md')
  )
  const pageSize = formMethods.watch(PAGE_SIZE_FIELD_NAME)

  // Track if we've received data for fade-in animation
  const hasReceivedData = useRef(false)
  const [showPaginationInfo, setShowPaginationInfo] = useState(false)

  const stylesWithProps = handleStylesWithProps({
    canGoBack,
    canGoForward,
    isLoading,
  })

  useEffect(() => {
    if (pageSize && onPageSizeChange) {
      onPageSizeChange(pageSize)
    }
  }, [pageSize, onPageSizeChange])

  // Trigger fade-in when data first loads (with actual results, not zeros)
  useEffect(() => {
    // Only show pagination info once we have real data loaded
    // Check for totalResultsQuantity > 0 OR (totalResultsQuantity === 0 AND not loading)
    // This ensures we wait for the actual API response, not initial zeros
    const hasRealData = !isLoading && totalResultsQuantity !== undefined && totalResultsQuantity > 0
    const hasEmptyResults = !isLoading && totalResultsQuantity === 0 && currentPageRecords === 0

    if ((hasRealData || hasEmptyResults) && !hasReceivedData.current) {
      hasReceivedData.current = true
      requestAnimationFrame(() => {
        setShowPaginationInfo(true)
      })
    }
  }, [isLoading, totalResultsQuantity, currentPageRecords])

  // Calculate display values
  const startRecord =
    totalResultsQuantity > 0 && currentPageRecords > 0
      ? Math.min(page * pageSize + 1, totalResultsQuantity)
      : 0
  const endRecord =
    totalResultsQuantity > 0 && currentPageRecords > 0
      ? Math.min(
          currentPageRecords === pageSize
            ? pageSize * (page + 1)
            : currentPageRecords + pageSize * page,
          totalResultsQuantity
        )
      : 0
  const totalDisplay = totalResultsQuantity || 0

  return (
    <Box sx={styles.paginationContainer}>
      {pageSizeOptions?.length > 0 && (
        <>
          {isTablet && (
            <SvgIcon
              test-id={`table-pagination-angleLeftIcon`}
              component={AngleLeftIcon}
              sx={stylesWithProps.goBackIcon}
              onClick={() => {
                if (canGoBack && !isLoading) {
                  onPreviousPage()
                }
              }}
            />
          )}
          <Box sx={styles.rowsPerPageContainer}>
            <Typography typography="medium" sx={styles.rowsPerPageLabel}>
              {t('components.table.pagination.rowsPerPage')}
            </Typography>
            <Box
              sx={[styles.paginationSelectContainer, hidePaginationUI && styles.hidePaginationUI]}
            >
              <FormProvider {...formMethods}>
                <form>
                  {!hideSelectPage && (
                    <TablePaginationSelect options={pageSizeOptions} name={PAGE_SIZE_FIELD_NAME} />
                  )}
                </form>
              </FormProvider>
            </Box>
          </Box>
        </>
      )}
      {/* Pagination info - fixed width container with fade-in */}
      <Box sx={styles.rowsPerPageContainer}>
        <Box
          sx={[
            styles.pageQuantityContainer,
            hidePaginationUI && styles.hidePaginationUI,
            showPaginationInfo ? styles.pageQuantityFadeIn : styles.pageQuantityHidden,
          ]}
        >
          <Typography typography="header4" sx={styles.pageQuantityLabel}>
            {startRecord} - {endRecord}
          </Typography>
          <Typography typography="medium" color="gray.64">
            {t('components.table.pagination.of')}
          </Typography>
          <Typography typography="header4" color="gray.64">
            {totalDisplay}
          </Typography>
        </Box>
        <Box sx={styles.paginationIconsContainer}>
          {!isTablet && (
            <SvgIcon
              test-id={`table-pagination-angleLeftIcon`}
              component={AngleLeftIcon}
              sx={stylesWithProps.goBackIcon}
              onClick={() => {
                if (canGoBack && !isLoading) {
                  onPreviousPage()
                }
              }}
            />
          )}
          <SvgIcon
            test-id={`table-pagination-angleRightIcon`}
            component={AngleRightIcon}
            sx={stylesWithProps.goForwardIcon}
            onClick={() => {
              if (canGoForward && !isLoading) {
                onNextPage()
              }
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}

TablePagination.propTypes = {
  page: PropTypes.number.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  pageSizeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    })
  ),
  canGoBack: PropTypes.bool.isRequired,
  canGoForward: PropTypes.bool.isRequired,
  onNextPage: PropTypes.func.isRequired,
  onPreviousPage: PropTypes.func.isRequired,
  totalResultsQuantity: PropTypes.number,
  currentPageRecords: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  hidePaginationUI: PropTypes.bool,
  hideSelectPage: PropTypes.bool,
}

TablePagination.defaultValues = {
  hidePaginationUI: false,
}

export default TablePagination
